import { format } from 'date-fns';
import { answerText, parseAnswerPairKey, questionText } from './columns';
import { balanceDue, documentChecklist, getPaymentState, medicalReviewLabel } from './registrations';

/*
 * CSV export of the given rows and visible columns (docs/director-reporting-prototype.md, P8).
 *
 * - Booking # always comes first; the rest follow the table's visible columns, in order.
 * - Guardians and payment spread over several columns, so Excel can sort and sum them.
 * - Money is the data's own figures, as plain numbers. Booking figures repeat on every row of a booking:
 *   "Participants on booking" says how many rows share them.
 * - UTF-8 with a BOM and CRLF, which Excel opens without an import dialog. Text starting with
 *   = + - @ gets a leading apostrophe, so answers typed by parents can't run as formulas.
 */

const money = value => (value === null || value === undefined || value === '' ? '' : (Math.round(Number(value) * 100) / 100).toFixed(2));
const day = ms => (ms ? format(new Date(ms), 'yyyy-MM-dd') : '');
const dateTime = ms => (ms ? format(new Date(ms), 'yyyy-MM-dd HH:mm') : '');
// DOB is a calendar day stored at UTC midnight (utils/format formatDay).
const utcDay = ms => {
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};
const yesNo = value => (value ? 'Yes' : 'No');

// Each column key becomes one or more [header, value(row), isNumber] cells.
const text = (header, value) => [[header, value]];

/*
 * Phone numbers are stored as "+12345678901". Plain, Excel reads that as a number (drops the +, shows
 * 1.23457E+10), and the formula guard below would show it as '+1234... So a value that is only a
 * phone number (digits, spaces, + - ( ) .) is written as ="+12345678901", which Excel shows exactly
 * as stored; the coupons export does the same for its dates. Anything else in the field stays text
 * and keeps the guard.
 */
const PHONE = /^(?=.*\d)\+?[\d\s().-]+$/;
const phone = value => {
  const s = String(value ?? '').trim();
  return PHONE.test(s) ? { formula: `="${s}"` } : s;
};
const num = (header, value) => [[header, value, true]];

const columnCells = {
  bookingDate: () => [['Booking date', r => dateTime(r.bookingDate)]],
  participant: () => [['Participant first name', r => r.participant.firstName], ['Participant last name', r => r.participant.lastName]],
  bookingNumber: () => [],
  program: () => [['Program', r => r.program.programName], ['Program start', r => utcDay(r.program.startDate)]],
  status: () => text('Status', r => r.status),
  statusNote: () => text('Status note', r => r.statusNote),
  stay: () => text('Commuter / Overnight', r => r.stay),
  age: () => num('Age', r => r.participant.age ?? ''),
  grade: () => text('Grade', r => r.participant.grade),
  dob: () => text('Date of birth', r => utcDay(r.participant.dob)),
  gender: () => text('Gender', r => r.participant.gender),
  primaryGuardian: () => [
    ['Primary guardian', r => r.primaryGuardian.name],
    ['Primary guardian phone', r => phone(r.primaryGuardian.phone)],
    ['Primary guardian email', r => r.primaryGuardian.email],
  ],
  secondaryGuardian: () => [
    ['Secondary guardian', r => r.secondaryGuardian?.name ?? ''],
    ['Secondary guardian phone', r => phone(r.secondaryGuardian?.phone)],
    ['Secondary guardian email', r => r.secondaryGuardian?.email ?? ''],
  ],
  address: () => text('Address', r => r.address.street),
  apt: () => text('Apartment #', r => r.address.apt),
  city: () => text('City', r => r.address.city),
  state: () => text('State', r => r.address.state),
  zip: () => text('Zip code', r => r.address.zip),
  extras: () => text('Additional experience', r => r.extras.map(e => e.title).filter(Boolean).join('; ')),
  additionalCharge: () => num('Additional charge', r => money(r.money?.participantFees?.additionalCharges || 0)),
  tshirtSize: () => text('T-shirt size', r => r.participant.tshirtSize),
  shortsSize: () => text('Shorts size', r => r.participant.shortsSize),
  payment: () => [
    ['Payment status', r => getPaymentState(r)],
    ['Booking total', r => money(r.money?.booking?.total), true],
    ['Paid', r => money(r.money?.booking?.paid), true],
    ['Balance due', r => money(balanceDue(r)), true],
    ['Participants on booking', r => r.money?.booking?.participantsOnBooking ?? 1, true],
  ],
  paymentType: () => [
    ['Payment type', r => r.money?.booking?.paymentMethod || ''],
    ['Payment plan', r => yesNo(r.money?.booking?.isPaymentPlan)],
  ],
  couponCode: () => text('Coupon code', r => r.money?.booking?.couponCode || ''),
  couponAmount: () => num('Coupon amount', r => money(r.money?.participantFees?.couponDiscount || 0)),
  insuranceCompany: () => text('Insurance company', r => r.participant.insuranceCompany),
  documents: () => [
    ['Documents received', r => {
      const c = documentChecklist(r);
      return `${c.received} of ${c.required}`;
    }],
    ['Documents missing', r => (r.documents?.missing || []).join('; ')],
  ],
  medicalReview: () => text('Medical review', medicalReviewLabel),
  medical: () => text('Medical', r => r.participant.medical),
  allergy: () => [['Allergy', r => r.participant.allergy], ['Allergy life-threatening', r => (Number(r.participant.allergyDeadly) === 1 ? 'Yes' : '')]],
  groupCode: () => text('Group code', r => r.participant.groupCode),
};

// Question N / Answer N: the row's Nth stored answer, headed as in the table and the master export.
function answerPairCells(key) {
  const pair = parseAnswerPairKey(key);
  if (!pair) return [];
  return pair.kind === 'question' ? text(`Question ${pair.n}`, r => questionText(r, pair.n)) : text(`Answer ${pair.n}`, r => answerText(r, pair.n));
}

export function csvCells(keys) {
  const cells = [['Booking #', r => r.bookingNumber]];
  keys.forEach(key => {
    cells.push(...(columnCells[key] ? columnCells[key]() : answerPairCells(key)));
  });
  return cells;
}

// { formula } is a cell this file built itself (phone above): written as is, not guarded.
const FORMULA_START = /^[=+\-@\t\r]/;
export function csvEscape(value, isNumber = false) {
  if (value && typeof value === 'object' && 'formula' in value) {
    const f = value.formula;
    return /[",\r\n]/.test(f) ? `"${f.replace(/"/g, '""')}"` : f;
  }
  let s = value === null || value === undefined ? '' : String(value);
  if (!isNumber && FORMULA_START.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildRegistrationsCsv(rows, keys) {
  const cells = csvCells(keys);
  const lines = [cells.map(([header]) => csvEscape(header)).join(',')];
  rows.forEach(r => lines.push(cells.map(([, value, isNumber]) => csvEscape(value(r), isNumber)).join(',')));
  return `﻿${lines.join('\r\n')}\r\n`;
}

export function csvFilename(campCode, year, scope) {
  return `registrations-${campCode}-${year}-${scope}-${day(Date.now())}.csv`;
}

export function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
